import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import DataSourceBadge from '../components/DataSourceBadge';

export default function DomainOverviewPage() {
  const [params] = useSearchParams();
  const initialDomain = params.get('domain') || '';
  const [domain, setDomain] = useState(initialDomain);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!domain.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/domain/overview', { domain });
      setData(res.data.data);
      setMeta({ isReal: res.data.isReal, dataSource: res.data.dataSource });
      updateCredits(res.data.credits_remaining);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to analyze domain');
    } finally { setLoading(false); }
  }

  useEffect(() => { if (initialDomain) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="page-title">Domain Overview</h1>
            <p className="page-sub">Get authority score, organic traffic, keywords, backlinks — like SEMrush Domain Overview · 3 credits</p>
          </div>
          {meta && <DataSourceBadge isReal={meta.isReal} dataSource={meta.dataSource} />}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter domain e.g. nike.com, semrush.com, nytimes.com" value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !domain.trim()} style={{ flexShrink: 0 }}>
            {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.3)' }} /> Analyzing...</> : 'Analyze →'}
          </button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && (
        <div className="card"><div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>{[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 100 }} />)}</div></div>
      )}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, marginBottom: 16 }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', marginBottom: 12 }}>Authority Score</div>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <div className={`authority-score ${data.authorityScore > 70 ? 'as-high' : data.authorityScore > 40 ? 'as-medium' : 'as-low'}`} style={{ width: 90, height: 90 }}>
                  <span style={{ fontSize: 28 }}>{data.authorityScore}</span>
                  <span style={{ fontSize: 10, fontWeight: 600 }}> / 100</span>
                </div>
              </div>
              <div className={`badge ${data.authorityScore > 70 ? 'badge-green' : data.authorityScore > 40 ? 'badge-yellow' : 'badge-red'}`}>{data.authorityScoreLevel}</div>
              <div style={{ marginTop: 12, fontSize: 11, color: '#6B7280' }}>Semrush Rank #{data.semrushRank.toLocaleString()}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {[
                { label: 'Organic Search Traffic', value: data.organicTrafficFormatted, sub: `${data.organicKeywordsFormatted} keywords`, trend: data.trafficTrend, icon: '◫' },
                { label: 'Paid Search Traffic', value: data.paidTraffic ? `${(data.paidTraffic/1000).toFixed(1)}K` : '0', sub: `${data.paidKeywords} keywords · $${data.paidTrafficCost.toLocaleString()} cost`, trend: 5, icon: '◧' },
                { label: 'Backlinks', value: data.backlinksFormatted, sub: `${data.refDomainsFormatted} ref. domains`, trend: data.backlinksTrend, icon: '🔗' },
              ].map((m,i) => (
                <div key={i} className="card" style={{ margin: 0 }}>
                  <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span>{m.icon}</span>{m.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="metric-value">{m.value}</div>
                    <span className={`metric-trend ${m.trend > 0 ? 'trend-up' : m.trend < 0 ? 'trend-down' : 'trend-neutral'}`}>{m.trend > 0 ? '↗' : '↘'} {Math.abs(m.trend)}%</span>
                  </div>
                  <div className="metric-sub">{m.sub}</div>
                </div>
              ))}
              <div className="card" style={{ margin: 0 }}>
                <div className="metric-label">Organic Traffic Cost</div>
                <div className="metric-value">${(data.organicTrafficCost/1000).toFixed(0)}K</div>
                <div className="metric-sub">traffic cost if paid via PPC</div>
              </div>
              <div className="card" style={{ margin: 0 }}>
                <div className="metric-label">Branded vs Non-Branded</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <div style={{ flex: data.brandedVsNonBranded.branded, background: '#111', height: 8, borderRadius: 4 }}></div>
                  <div style={{ flex: data.brandedVsNonBranded.nonBranded, background: '#E5E7EB', height: 8, borderRadius: 4 }}></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6, color: '#6B7280' }}>
                  <span>Branded {data.brandedVsNonBranded.branded}%</span>
                  <span>Non-branded {data.brandedVsNonBranded.nonBranded}%</span>
                </div>
              </div>
              <div className="card" style={{ margin: 0, background: '#FFF0EB', borderColor: '#FFD4C2' }}>
                <div className="metric-label" style={{ color: '#FF642D' }}>Main Organic Competitors</div>
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  {data.mainOrganicCompetitors.map(c => <div key={c.domain} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #FFE0D3' }}><span style={{ fontWeight: 600 }}>{c.domain}</span><span style={{ color: '#6B7280' }}>{c.commonKeywords} common kw</span></div>)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Organic Traffic Trend (12 months)</span></div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
                {data.organicTrafficTrend.map((m,i) => {
                  const max = Math.max(...data.organicTrafficTrend.map(x => x.traffic));
                  const h = (m.traffic / max) * 100;
                  return <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '100%', background: '#FF642D', height: `${h}%`, borderRadius: '3px 3px 0 0', minHeight: 4 }}></div>
                    <span style={{ fontSize: 10, color: '#6B7280' }}>{m.month}</span>
                  </div>
                })}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Traffic by Country</span></div>
              {data.topCountries.map(c => (
                <div key={c.code} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                  <span><span style={{ fontWeight: 700, marginRight: 6 }}>{c.code}</span>{c.country}</span>
                  <span style={{ color: '#6B7280' }}>{c.share}% · {(c.traffic/1000).toFixed(1)}K</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Organic Competitors ({data.competitors.length})</span><span className="badge badge-gray">Live data</span></div>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Competitor</th><th>Common Keywords</th><th>SE Keywords</th><th>Traffic</th><th>Authority</th></tr></thead>
                <tbody>
                  {data.competitors.map(c => (
                    <tr key={c.domain}><td style={{ fontWeight: 600 }}>{c.domain}</td><td>{c.commonKeywords.toLocaleString()}</td><td>{c.seKeywords.toLocaleString()}</td><td>{(c.traffic/1000).toFixed(1)}K</td><td><span className={`badge ${c.authorityScore > 70 ? 'badge-green' : c.authorityScore > 40 ? 'badge-yellow' : 'badge-red'}`}>{c.authorityScore}</span></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
